import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  FileText,
  Building,
  MapPin,
  DollarSign,
  Clock,
  CheckCircle2,
  AlertCircle,
  Search,
  Filter,
  ArrowRight,
  ExternalLink,
  Users,
  Calendar,
  Star,
  Briefcase,
  Plus,
  RefreshCw,
  Eye,
  X,
  Check,
  ChevronDown,
  ArrowUpDown,
  MoreHorizontal,
} from "lucide-react";
import { useToast } from "../components/shared/ToastProvider";
import { formatRequestId } from "../utils/idFormatter";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OpenRequests = () => {
  const { showToast } = useToast();
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterSource, setFilterSource] = useState("all");
  const [filterClient, setFilterClient] = useState("all");
  const [availableClients, setAvailableClients] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [converting, setConverting] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  // Removed showStatusChangeModal - no longer needed with direct status changes
  const [actionRequest, setActionRequest] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [actionNotes, setActionNotes] = useState("");
  // Removed newStatus and statusChangeReason - no longer needed
  const [processing, setProcessing] = useState(false);

  // Table controls
  const [sortField, setSortField] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [openActionMenus, setOpenActionMenus] = useState({});

  useEffect(() => {
    fetchOpenRequests();
    fetchClientNames();
  }, []);

  // Close action menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".relative")) {
        setOpenActionMenus({});
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    filterRequests();
  }, [requests, searchTerm, filterStatus, filterSource, filterClient]);

  const fetchOpenRequests = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/open-requests`);
      setRequests(response.data);

      // Extract unique client names for filter dropdown
      const clients = [
        ...new Set(
          response.data.map((request) => request.clientName).filter(Boolean)
        ),
      ];
      setAvailableClients(clients);
    } catch (error) {
      console.error("Error fetching open requests:", error);
      showToast("Failed to fetch open requests", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchClientNames = async () => {
    try {
      const response = await axios.get(`${API}/open-requests/clients`);
      setAvailableClients(response.data.clients || []);
    } catch (error) {
      console.error("Error fetching client names:", error);
      // Fallback to extracting from current requests
      const clients = [
        ...new Set(
          requests.map((request) => request.clientName).filter(Boolean)
        ),
      ];
      setAvailableClients(clients);
    }
  };

  const filterRequests = () => {
    let filtered = requests;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (request) =>
          request.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          request.department.toLowerCase().includes(searchTerm.toLowerCase()) ||
          request.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
          request.vendorPortal
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          (request.clientName &&
            request.clientName.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Status filter
    if (filterStatus !== "all") {
      filtered = filtered.filter((request) => request.status === filterStatus);
    }

    // Source filter
    if (filterSource !== "all") {
      filtered = filtered.filter(
        (request) => request.vendorPortal === filterSource
      );
    }

    // Client filter
    if (filterClient !== "all") {
      filtered = filtered.filter(
        (request) => request.clientName === filterClient
      );
    }

    setFilteredRequests(filtered);
  };

  const convertToJob = async (requestId) => {
    setConverting(true);
    try {
      // Get the current request to check status
      const currentRequest = requests.find((r) => r.id === requestId);

      if (!currentRequest) {
        throw new Error(
          "Request not found. Please refresh the page and try again."
        );
      }

      console.log(
        `Converting request ${requestId} with status: ${currentRequest.status}`
      );

      if (currentRequest.status === "converted") {
        throw new Error("This request has already been converted to a job.");
      }

      if (currentRequest.status === "new") {
        // If status is 'new', we need to approve first (which auto-converts)
        const actionData = {
          performed_by: "Current Recruiter",
          notes: "Auto-approved for job conversion",
        };
        console.log("Auto-approving new request for conversion...");
        await axios.post(
          `${API}/open-requests/${requestId}/approve`,
          actionData
        );
        showToast(
          "Request approved and converted to job requisition successfully!",
          "success"
        );
      } else if (currentRequest.status === "approved") {
        // If already approved, just convert
        console.log("Converting approved request to job...");
        const response = await axios.post(
          `${API}/open-requests/${requestId}/convert`
        );
        console.log("Conversion response:", response.data);
        showToast(
          "Request successfully converted to job requisition!",
          "success"
        );
      } else if (currentRequest.status === "rejected") {
        throw new Error(
          "Cannot convert rejected requests. Please approve the request first using the status change menu."
        );
      } else if (currentRequest.status === "reviewed") {
        throw new Error(
          "Cannot convert reviewed requests. Please approve the request first using the status change menu."
        );
      } else {
        throw new Error(
          `Cannot convert request with status "${currentRequest.status}". Only approved requests can be converted to jobs.`
        );
      }

      setShowRequestModal(false);
      fetchRequests(); // Refresh the list
    } catch (error) {
      console.error("Error converting request:", error);

      // Enhanced error messaging
      let errorMessage = "Failed to convert request to job";

      if (error.response?.status === 400) {
        errorMessage =
          error.response.data?.detail ||
          "Bad request - please check the request status and try again";
      } else if (error.response?.status === 404) {
        errorMessage = "Request not found - it may have been deleted or moved";
      } else if (error.response?.status === 500) {
        errorMessage =
          "Server error occurred during conversion. Please try again in a few moments.";
      } else if (error.message) {
        errorMessage = error.message;
      }

      showToast(errorMessage, "error");
    } finally {
      setConverting(false);
    }
  };

  const handleApprove = (request) => {
    setActionRequest(request);
    setActionNotes("");
    setShowApproveModal(true);
  };

  const handleReject = (request) => {
    setActionRequest(request);
    setRejectReason("");
    setActionNotes("");
    setShowRejectModal(true);
  };

  const approveRequest = async () => {
    if (!actionRequest) return;

    setProcessing(true);
    try {
      const actionData = {
        action: "approve",
        performed_by: "Current Recruiter", // In real app, get from auth context
        notes: actionNotes,
      };

      await axios.post(
        `${API}/open-requests/${actionRequest.id}/approve`,
        actionData
      );
      showToast("Request approved and converted to job successfully");
      setShowApproveModal(false);
      fetchOpenRequests(); // Refresh the list
    } catch (error) {
      console.error("Error approving request:", error);
      showToast(
        error.response?.data?.detail || "Failed to approve request",
        "error"
      );
    } finally {
      setProcessing(false);
    }
  };

  const rejectRequest = async () => {
    if (!actionRequest || !rejectReason.trim()) {
      showToast("Please provide a reason for rejection", "warning");
      return;
    }

    setProcessing(true);
    try {
      const actionData = {
        action: "reject",
        performed_by: "Current Recruiter", // In real app, get from auth context
        reason: rejectReason,
        notes: actionNotes,
      };

      await axios.post(
        `${API}/open-requests/${actionRequest.id}/reject`,
        actionData
      );
      showToast("Request rejected successfully");
      setShowRejectModal(false);
      fetchOpenRequests(); // Refresh the list
    } catch (error) {
      console.error("Error rejecting request:", error);
      showToast(
        error.response?.data?.detail || "Failed to reject request",
        "error"
      );
    } finally {
      setProcessing(false);
    }
  };

  // Available statuses for change
  const availableStatuses = [
    { value: "new", label: "New", description: "Newly received request" },
    {
      value: "reviewed",
      label: "Reviewed",
      description: "Request has been reviewed",
    },
    {
      value: "approved",
      label: "Approved",
      description: "Request approved and ready for conversion",
    },
    { value: "rejected", label: "Rejected", description: "Request rejected" },
    {
      value: "converted",
      label: "Converted",
      description: "Converted to job requisition",
    },
  ];

  // Handle status change
  const handleStatusChange = async (request, newStatusValue) => {
    try {
      setProcessing(true);

      const actionData = {
        performed_by: "Sales Team",
        reason:
          newStatusValue === "approved"
            ? "Request approved for recruiting"
            : "Request rejected - not viable",
      };

      let endpoint = "";
      let successMessage = "";

      if (newStatusValue === "approved") {
        endpoint = `${API}/open-requests/${request.id}/approve`;
        successMessage = "Request approved and job created successfully!";
      } else if (newStatusValue === "rejected") {
        endpoint = `${API}/open-requests/${request.id}/reject`;
        successMessage = "Request rejected successfully";
      } else {
        throw new Error(`Invalid status: ${newStatusValue}`);
      }

      const response = await axios.post(endpoint, actionData);

      // If approved, show job creation success
      if (newStatusValue === "approved" && response.data.job_id) {
        showToast(
          `${successMessage} Job ID: ${response.data.job_id.substring(
            0,
            8
          )}...`,
          "success"
        );
      } else {
        showToast(successMessage, "success");
      }

      fetchOpenRequests(); // Refresh the list
    } catch (error) {
      console.error(`Error changing status to ${newStatusValue}:`, error);
      let errorMessage = `Failed to ${newStatusValue} request`;

      if (error.response?.status === 400) {
        errorMessage =
          error.response.data?.detail ||
          "Bad request - please check the request and try again";
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }

      showToast(errorMessage, "error");
    } finally {
      setProcessing(false);
    }
  };

  // Removed old changeRequestStatus - now using direct handleStatusChange

  const updateRequestStatus = async (requestId, newStatus) => {
    try {
      await axios.put(`${API}/open-requests/${requestId}/status`, {
        status: newStatus,
      });
      showToast(`Request status updated to ${newStatus}`);
      fetchOpenRequests();
    } catch (error) {
      console.error("Error updating request status:", error);
      showToast("Failed to update request status", "error");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "new":
        return "bg-blue-100 text-blue-800";
      case "approved":
        return "bg-green-100 text-green-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getSourceIcon = (source) => {
    if (!source) return <FileText className="h-4 w-4 text-gray-600" />;

    switch (source.toLowerCase()) {
      case "beeline":
        return <Building className="h-4 w-4 text-blue-600" />;
      case "agileone":
        return <Users className="h-4 w-4 text-green-600" />;
      case "sapfieldglass":
        return <ExternalLink className="h-4 w-4 text-purple-600" />;
      default:
        return <FileText className="h-4 w-4 text-gray-600" />;
    }
  };

  // Table helper functions
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Toggle actions menu for specific request
  const toggleActionMenu = (requestId) => {
    setOpenActionMenus((prev) => ({
      ...prev,
      [requestId]: !prev[requestId],
    }));
  };

  // Handle action clicks from table
  const handleTableActionClick = (requestId, actionType) => {
    setOpenActionMenus((prev) => ({ ...prev, [requestId]: false }));
    const request = requests.find((r) => r.id === requestId);

    switch (actionType) {
      case "view":
        setSelectedRequest(request);
        setShowRequestModal(true);
        break;
      case "approve":
        setActionRequest(request);
        setShowApproveModal(true);
        break;
      case "reject":
        setActionRequest(request);
        setShowRejectModal(true);
        break;
      case "convert":
        convertToJob(request.id);
        break;
      default:
        showToast("Action not implemented", "warning");
    }
  };

  // Filter and sort requests for table
  const sortedRequests = [...filteredRequests].sort((a, b) => {
    // First priority: New requests always at top
    if (a.status === "new" && b.status !== "new") return -1;
    if (b.status === "new" && a.status !== "new") return 1;

    // Second priority: Chronological order by date (newest first)
    const aDate = new Date(a.createdAt || a.updatedAt);
    const bDate = new Date(b.createdAt || b.updatedAt);

    // If we're sorting by createdAt specifically, respect sort direction
    if (sortField === "createdAt") {
      return sortDirection === "asc" ? aDate - bDate : bDate - aDate;
    }

    // Default: newest first (descending chronological)
    return bDate - aDate;
  });

  // Pagination
  const totalPages = Math.ceil(sortedRequests.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedRequests = sortedRequests.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  const RequestCard = ({ request }) => (
    <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow">
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-2">
              {getSourceIcon(request.vendorPortal)}
              <span className="text-sm text-gray-500 font-medium">
                {request.vendorPortal || "Unknown Source"}
              </span>
              <span
                className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                  request.status
                )}`}
              >
                {request.status}
              </span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {request.title || "Untitled Request"}
            </h3>
            <div className="flex items-center text-sm text-gray-600 space-x-4 mb-3">
              <div className="flex items-center space-x-1">
                <Building className="h-4 w-4" />
                <span>{request.department || "N/A"}</span>
              </div>
              <div className="flex items-center space-x-1">
                <MapPin className="h-4 w-4" />
                <span>{request.location || "N/A"}</span>
              </div>
              {request.clientName && (
                <div className="flex items-center space-x-1">
                  <Users className="h-4 w-4" />
                  <span>{request.clientName}</span>
                </div>
              )}
              {request.salaryRange && (
                <div className="flex items-center space-x-1">
                  <DollarSign className="h-4 w-4" />
                  <span>
                    ${request.salaryRange.min?.toLocaleString()} - $
                    {request.salaryRange.max?.toLocaleString()}
                  </span>
                </div>
              )}
            </div>
            <div className="flex items-center text-sm text-gray-500 space-x-4">
              <div className="flex items-center space-x-1">
                <Calendar className="h-4 w-4" />
                <span>
                  Received: {new Date(request.createdAt).toLocaleDateString()}
                </span>
              </div>
              <div className="flex items-center space-x-1">
                <Clock className="h-4 w-4" />
                <span>Priority: {request.priority}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2 ml-4">
            <button
              onClick={() => {
                setSelectedRequest(request);
                setShowRequestModal(true);
                showToast(`Viewing details for "${request.title}"`);
              }}
              className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="View Details"
            >
              <Eye className="h-5 w-5" />
            </button>

            {request.status === "new" && (
              <>
                <button
                  onClick={() => handleApprove(request)}
                  className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
                  disabled={processing}
                >
                  <Check className="h-4 w-4" />
                  <span>Approve</span>
                </button>

                <button
                  onClick={() => handleReject(request)}
                  className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2"
                  disabled={processing}
                >
                  <X className="h-4 w-4" />
                  <span>Reject</span>
                </button>
              </>
            )}

            {request.status === "approved" && (
              <button
                onClick={() => convertToJob(request.id)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
                disabled={converting}
              >
                <Plus className="h-4 w-4" />
                <span>{converting ? "Converting..." : "Convert to Job"}</span>
              </button>
            )}
          </div>
        </div>

        {request.requiredSkills && request.requiredSkills.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {request.requiredSkills.slice(0, 5).map((skill, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
              >
                {skill}
              </span>
            ))}
            {request.requiredSkills.length > 5 && (
              <span className="text-gray-500 text-sm">
                +{request.requiredSkills.length - 5} more
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );

  const RequestDetailModal = React.useCallback(
    () => (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            console.log("Backdrop clicked");
            setShowRequestModal(false);
          }
        }}
      >
        <div
          className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {selectedRequest?.title}
                </h2>
                <div className="flex items-center space-x-3 mt-2">
                  {getSourceIcon(selectedRequest?.vendorPortal)}
                  <span className="text-gray-600">
                    {selectedRequest?.vendorPortal}
                  </span>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                      selectedRequest?.status
                    )}`}
                  >
                    {selectedRequest?.status}
                  </span>
                </div>
              </div>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log("Close button clicked");
                  setShowRequestModal(false);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Basic Information
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Department
                    </label>
                    <p className="text-gray-900">
                      {selectedRequest?.department}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Location
                    </label>
                    <p className="text-gray-900">{selectedRequest?.location}</p>
                  </div>
                  {selectedRequest?.clientName && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Client
                      </label>
                      <p className="text-gray-900">
                        {selectedRequest?.clientName}
                      </p>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Employment Type
                    </label>
                    <p className="text-gray-900">
                      {selectedRequest?.employmentType}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Experience Level
                    </label>
                    <p className="text-gray-900">
                      {selectedRequest?.experienceLevel}
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Request Details
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Priority
                    </label>
                    <p className="text-gray-900">{selectedRequest?.priority}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Hiring Manager
                    </label>
                    <p className="text-gray-900">
                      {selectedRequest?.hiringManager}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Requested Date
                    </label>
                    <p className="text-gray-900">
                      {selectedRequest?.createdAt &&
                        new Date(
                          selectedRequest.createdAt
                        ).toLocaleDateString()}
                    </p>
                  </div>
                  {selectedRequest?.salaryRange && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Salary Range
                      </label>
                      <p className="text-gray-900">
                        ${selectedRequest.salaryRange.min?.toLocaleString()} - $
                        {selectedRequest.salaryRange.max?.toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Job Description */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Job Description
              </h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-700 whitespace-pre-wrap">
                  {selectedRequest?.description}
                </p>
              </div>
            </div>

            {/* Skills */}
            {selectedRequest?.requiredSkills &&
              selectedRequest.requiredSkills.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    Required Skills
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedRequest.requiredSkills.map((skill, index) => (
                      <span
                        key={index}
                        className="px-3 py-2 bg-blue-100 text-blue-800 rounded-lg text-sm font-medium"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}

            {/* Audit Trail */}
            {selectedRequest?.auditTrail &&
              selectedRequest.auditTrail.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    Audit Trail
                  </h3>
                  <div className="space-y-3">
                    {selectedRequest.auditTrail.map((entry, index) => (
                      <div
                        key={index}
                        className="border border-gray-200 rounded-lg p-4"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${
                                entry.action === "approved"
                                  ? "bg-green-100 text-green-800"
                                  : entry.action === "rejected"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-blue-100 text-blue-800"
                              }`}
                            >
                              {entry.action}
                            </span>
                            <span className="text-sm font-medium text-gray-900">
                              by {entry.performed_by}
                            </span>
                          </div>
                          <span className="text-xs text-gray-500">
                            {entry.performed_at &&
                              new Date(entry.performed_at).toLocaleString()}
                          </span>
                        </div>

                        {entry.reason && (
                          <div className="mb-2">
                            <span className="text-sm font-medium text-gray-700">
                              Reason:{" "}
                            </span>
                            <span className="text-sm text-gray-600">
                              {entry.reason}
                            </span>
                          </div>
                        )}

                        {entry.notes && (
                          <div>
                            <span className="text-sm font-medium text-gray-700">
                              Notes:{" "}
                            </span>
                            <span className="text-sm text-gray-600">
                              {entry.notes}
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

            {/* Simple Status Actions */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-900">Status</h3>
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
                    selectedRequest?.status
                  )}`}
                >
                  {selectedRequest?.status}
                </span>
              </div>

              {/* Simple Action Buttons */}
              <div className="flex flex-wrap gap-2">
                {/* New requests: can approve or reject */}
                {selectedRequest?.status === "new" && (
                  <>
                    <button
                      onClick={() => {
                        setShowRequestModal(false);
                        handleStatusChange(selectedRequest, "approved");
                      }}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2 text-sm"
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      <span>Approve & Create Job</span>
                    </button>
                    <button
                      onClick={() => {
                        setShowRequestModal(false);
                        handleStatusChange(selectedRequest, "rejected");
                      }}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2 text-sm"
                    >
                      <X className="w-4 h-4" />
                      <span>Reject</span>
                    </button>
                  </>
                )}

                {/* Rejected requests: can only approve */}
                {selectedRequest?.status === "rejected" && (
                  <button
                    onClick={() => {
                      setShowRequestModal(false);
                      handleStatusChange(selectedRequest, "approved");
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2 text-sm"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Approve & Create Job</span>
                  </button>
                )}

                {/* Approved requests: view job only */}
                {selectedRequest?.status === "approved" &&
                  selectedRequest?.job_id && (
                    <button
                      onClick={() => {
                        setShowRequestModal(false);
                        window.location.href = `/jobs/${selectedRequest.job_id}`;
                      }}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center space-x-2 text-sm"
                    >
                      <ArrowRight className="w-4 h-4" />
                      <span>View Created Job</span>
                    </button>
                  )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-between items-center pt-6 border-t border-gray-200">
              <div className="flex space-x-3">
                {/* Additional actions can go here */}
              </div>

              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log("Close button (bottom) clicked");
                  setShowRequestModal(false);
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    ),
    [selectedRequest, setShowRequestModal, converting]
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Open Requests</h1>
          <p className="text-gray-600 mt-1">
            Manage job requisition requests from vendor portals
          </p>
        </div>
        <button
          onClick={fetchOpenRequests}
          disabled={loading}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium flex items-center space-x-2 shadow-lg disabled:opacity-50"
        >
          <RefreshCw className={`h-5 w-5 ${loading ? "animate-spin" : ""}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[300px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search requests..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Status</option>
            <option value="new">New</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="rejected">Rejected</option>
            <option value="converted">Converted</option>
          </select>

          <select
            value={filterSource}
            onChange={(e) => setFilterSource(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Sources</option>
            <option value="beeline">Beeline</option>
            <option value="agileone">Agile One</option>
            <option value="sapfieldglass">SAP FieldGlass</option>
          </select>

          <select
            value={filterClient}
            onChange={(e) => setFilterClient(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Clients</option>
            {availableClients.map((client) => (
              <option key={client} value={client}>
                {client}
              </option>
            ))}
          </select>

          <div className="flex items-center space-x-2">
            <FileText className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-600">
              {sortedRequests.length} requests
            </span>
          </div>
        </div>
      </div>

      {/* Open Requests Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : sortedRequests.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <FileText className="h-16 w-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            No requests found
          </h3>
          <p className="text-gray-600 mb-6">
            {requests.length === 0
              ? "Requests from vendor portals will appear here"
              : "No requests match your current filters"}
          </p>
          {requests.length > 0 && (
            <button
              onClick={() => {
                setSearchTerm("");
                setFilterStatus("all");
                setFilterSource("all");
                setFilterClient("all");
                showToast("Filters cleared");
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium inline-flex items-center space-x-2"
            >
              <X className="h-5 w-5" />
              <span>Clear Filters</span>
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input type="checkbox" className="rounded" />
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort("id")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>ID</span>
                      <ArrowUpDown className="w-4 h-4" />
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <div className="flex items-center space-x-1">
                      <span>Source</span>
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort("title")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Title</span>
                      <ArrowUpDown className="w-4 h-4" />
                      <Filter className="w-4 h-4" />
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort("clientName")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Client</span>
                      <ArrowUpDown className="w-4 h-4" />
                      <Filter className="w-4 h-4" />
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort("department")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Department</span>
                      <ArrowUpDown className="w-4 h-4" />
                      <Filter className="w-4 h-4" />
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort("status")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Status</span>
                      <ArrowUpDown className="w-4 h-4" />
                      <Filter className="w-4 h-4" />
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort("priority")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Priority</span>
                      <ArrowUpDown className="w-4 h-4" />
                      <Filter className="w-4 h-4" />
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort("createdAt")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Date Received</span>
                      <ArrowUpDown className="w-4 h-4" />
                      <Filter className="w-4 h-4" />
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort("hiringManager")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Hiring Manager</span>
                      <ArrowUpDown className="w-4 h-4" />
                      <Filter className="w-4 h-4" />
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <span>Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedRequests.map((request, index) => (
                  <tr
                    key={request.id}
                    className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input type="checkbox" className="rounded" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                      {formatRequestId(request.id)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center space-x-2">
                        {getSourceIcon(request.vendorPortal)}
                        <span className="text-xs text-gray-600">
                          {request.vendorPortal || "Unknown"}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => {
                          setSelectedRequest(request);
                          setShowRequestModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                      >
                        {request.title || "Untitled Request"}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {request.clientName || "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {request.department || "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                          request.status
                        )}`}
                      >
                        {request.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                          request.priority === "high"
                            ? "bg-red-100 text-red-800"
                            : request.priority === "medium"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-green-100 text-green-800"
                        }`}
                      >
                        {request.priority || "medium"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(request.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {request.hiringManager || "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="relative">
                        <button
                          onClick={() => toggleActionMenu(request.id)}
                          className="flex items-center space-x-1 px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-lg"
                        >
                          <MoreHorizontal className="w-4 h-4" />
                        </button>

                        {openActionMenus[request.id] && (
                          <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg py-2 z-50">
                            <button
                              onClick={() =>
                                handleTableActionClick(request.id, "view")
                              }
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                            >
                              <Eye className="w-4 h-4" />
                              <span>View Details</span>
                            </button>

                            {/* Directional Status Actions */}
                            <div className="border-t border-gray-200 my-1"></div>

                            {/* New requests: can approve or reject */}
                            {request.status === "new" && (
                              <>
                                <button
                                  onClick={() => {
                                    setOpenActionMenus({});
                                    handleStatusChange(request, "approved");
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                                >
                                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                                  <span>Approve & Create Job</span>
                                </button>
                                <button
                                  onClick={() => {
                                    setOpenActionMenus({});
                                    handleStatusChange(request, "rejected");
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                                >
                                  <X className="w-4 h-4 text-red-600" />
                                  <span>Reject</span>
                                </button>
                              </>
                            )}

                            {/* Rejected requests: can only approve */}
                            {request.status === "rejected" && (
                              <button
                                onClick={() => {
                                  setOpenActionMenus({});
                                  handleStatusChange(request, "approved");
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                              >
                                <CheckCircle2 className="w-4 h-4 text-green-600" />
                                <span>Approve & Create Job</span>
                              </button>
                            )}

                            {/* Approved requests: can only view job */}
                            {request.status === "approved" &&
                              request.job_id && (
                                <button
                                  onClick={() => {
                                    setOpenActionMenus({});
                                    // Navigate to job view
                                    window.location.href = `/jobs/${request.job_id}`;
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                                >
                                  <ArrowRight className="w-4 h-4 text-indigo-600" />
                                  <span>View Created Job</span>
                                </button>
                              )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="bg-white px-6 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700">Items per page:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                className="border border-gray-300 rounded px-2 py-1 text-sm"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <ChevronDown className="w-4 h-4 text-gray-500" />
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
              >
                ‹
              </button>
              <span className="px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded">
                {currentPage}
              </span>
              <button
                onClick={() =>
                  setCurrentPage(Math.min(totalPages, currentPage + 1))
                }
                disabled={currentPage === totalPages}
                className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
              >
                ›
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Request Detail Modal */}
      {showRequestModal && selectedRequest && <RequestDetailModal />}

      {/* Approve Request Modal */}
      {showApproveModal && actionRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                Approve Request
              </h2>
              <p className="text-gray-600 mt-1">
                Convert "{actionRequest.title}" to a job requisition
              </p>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  value={actionNotes}
                  onChange={(e) => setActionNotes(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  rows="3"
                  placeholder="Add any notes about the approval..."
                />
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start space-x-2">
                  <Check className="h-5 w-5 text-green-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-green-800">
                      Approval Action
                    </h4>
                    <p className="text-sm text-green-700 mt-1">
                      This will create a new job requisition and mark the
                      request as approved.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex justify-end space-x-3">
              <button
                onClick={() => setShowApproveModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-white transition-colors"
                disabled={processing}
              >
                Cancel
              </button>
              <button
                onClick={approveRequest}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                disabled={processing}
              >
                {processing ? "Approving..." : "Approve & Convert"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Request Modal */}
      {showRejectModal && actionRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                Reject Request
              </h2>
              <p className="text-gray-600 mt-1">
                Reject "{actionRequest.title}" with audit trail
              </p>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Rejection <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  rows="3"
                  placeholder="Please provide a clear reason for rejecting this request..."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Notes (Optional)
                </label>
                <textarea
                  value={actionNotes}
                  onChange={(e) => setActionNotes(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  rows="2"
                  placeholder="Any additional notes..."
                />
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start space-x-2">
                  <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-red-800">
                      Rejection Action
                    </h4>
                    <p className="text-sm text-red-700 mt-1">
                      This action will be recorded in the audit trail and cannot
                      be undone.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex justify-end space-x-3">
              <button
                onClick={() => setShowRejectModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-white transition-colors"
                disabled={processing}
              >
                Cancel
              </button>
              <button
                onClick={rejectRequest}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                disabled={processing || !rejectReason.trim()}
              >
                {processing ? "Rejecting..." : "Reject Request"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status change is now handled directly - no modal needed */}
    </div>
  );
};

export default OpenRequests;
